/**
 * Clipboard access.
 *
 * The async Clipboard API is unavailable on insecure origins and inside some
 * embedded browsers, and it rejects when the document is not focused. A silent
 * failure previously left a copy button looking untouched, which reads as a dead
 * control, so the fallback path and the boolean result live in one place and
 * every caller renders the outcome.
 */

/**
 * Copies text, returning whether the browser accepted it.
 *
 * The hidden-textarea fallback is the only route on an insecure origin; it is
 * deprecated but still the only synchronous option there.
 */
export async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    // Fall through to the legacy path.
  }

  try {
    const area = document.createElement('textarea')
    area.value = value
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.opacity = '0'
    document.body.appendChild(area)
    area.select()
    const copied = document.execCommand('copy')
    document.body.removeChild(area)
    return copied
  } catch {
    return false
  }
}
